import React, { useMemo } from "react";
import styled from "styled-components";

// 型定義
interface Project {
  approved_shots_count: number;
  submitted_shots_count: number;
  total_shots_count: number;
  pending_shots_count: number;
}

interface ProgressMeterProps {
  project: Project;
  variant?: 'default' | 'banner' | 'compact';
  showDescription?: boolean;
  className?: string;
}

interface ProgressBarProps {
  $value: number;
  $max: number;
}

interface ProgressDescriptionProps {
  $variant: 'default' | 'banner' | 'compact';
}

const ProgressBarContainer = styled.div`
  position: relative;
  width: 100%;
  height: 4px;
  background-color: var(--soremo-border);
  border-radius: 4px;
  overflow: hidden;
  cursor: pointer;
`;

const BaseProgressBar = styled.div`
  position: absolute;
  height: 100%;
  transition: width 1.5s ease;
`;

const ApprovalProgress = styled(BaseProgressBar)<ProgressBarProps>`
  background-color: var(--soremo-blue);
  width: ${props => (props.$value / props.$max) * 100}%;
  z-index: 2;
`;

const CurrentProgress = styled(BaseProgressBar)<ProgressBarProps>`
  background-color: var(--soremo-gray);
  width: ${props => (props.$value / props.$max) * 100}%;
  z-index: 1;
`;

const ProgressDescription = styled.div<ProgressDescriptionProps>`
  margin-top: ${props => props.$variant === 'compact' ? '4px' : '8px'};
`;

const useProjectProgress = (project: Project) => {
  return useMemo(() => {
    const total = project.total_shots_count ?? 0;
    const submitted = project.submitted_shots_count ?? 0;
    const approved = project.approved_shots_count ?? 0;
    const hold = project.pending_shots_count ?? 0;
    const denominator = total - hold;

    const approvalRatio =
      total === 0
        ? 1
        : denominator <= 0
          ? 0
          : approved / denominator;
    const currentRatio = total > 0 ? submitted / total : 0;

    return {
      approvalRatio,
      currentRatio,
      approvalPercent: Math.round(approvalRatio * 1000) / 10,
      currentPercent: Math.round(currentRatio * 1000) / 10,
      isComplete: submitted === total,
      remainingDeliverables: Math.max(total - submitted, 0),
      remainingApprovals: denominator > 0 ? Math.max(denominator - approved, 0) : 0,
      total,
      submitted,
      approved,
    };
  }, [project]);
};

const ProgressMeter: React.FC<ProgressMeterProps> = ({ 
  project,
  variant = 'default',
  showDescription = true,
  className
}) => {
  const progress = useProjectProgress(project);
  
  const renderDescription = () => {
    if (!showDescription) return null;

    return (
      <ProgressDescription $variant={variant} className="u-col">
        <p className="bodytext-11 u-text-blue">
        {progress.approvalPercent}%
        </p>
        {!progress.isComplete ? (
          <p className="bodytext-11 u-text-light-gray">
            あと{progress.remainingDeliverables}件の納品が必要。
          </p>
        ) : (
          <p className="bodytext-11 u-text-light-gray">
            あと{progress.remainingApprovals}件のいいねが必要。
          </p>
        )}
      </ProgressDescription>
    );
  };

  return (
    <div className={className}>
      <ProgressBarContainer>
        <ApprovalProgress 
          $value={progress.approvalPercent}
          $max={100}
        />
        <CurrentProgress 
          $value={progress.currentPercent}
          $max={100}
        />
      </ProgressBarContainer>
      {renderDescription()}
    </div>
  );
};

export { useProjectProgress };
export default ProgressMeter; 